import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from './translations';
import { useAuth } from '../contexts/AuthContext';

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, updateUser } = useAuth();
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage === 'en' || savedLanguage === 'es') ? savedLanguage : 'es';
  });

  useEffect(() => {
    if (isAuthenticated && user?.language) {
      setLanguageState(user.language as Language);
    } else {
      const savedLanguage = localStorage.getItem('language');
      setLanguageState((savedLanguage === 'en' || savedLanguage === 'es') ? savedLanguage : 'es');
    }
  }, [isAuthenticated, user]);

  const setLanguage = (lang: Language) => {
    if (isAuthenticated) {
      console.log('Updating user language to:', lang);
      updateUser({ language: lang });
    } else {
      localStorage.setItem('language', lang);
      setLanguageState(lang);
    }
  };

  const t = (key: string, params?: Record<string, any>): string => {
    let text = (translations[language] as Record<string, string>)[key];
    
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        text = text.replace(`{${paramKey}}`, String(paramValue));
      });
    }
    
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 